import { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '../../../../core/components/molecules/Typography';
import { useLesson } from '../../hooks/useLesson';
import { useLessonStore } from '../../stores/lessonStore';
import { formatTime } from '../../../../core/commom/formatTime';
import { Colors, ILesson } from '../../../../core/models/disciplines';
import { useNewSubjectStore } from '../../stores/newSubjectStore';
import { useFilterConfigStore } from '../../stores/filterConfig';
import { IResponseFavorite } from '../../hooks/useContentFavorite';
import { useLessonContentStore } from '../../stores/lessonContentStore';
import { useFinishLesson } from '../../../../core/hooks/useFinishLesson';
import { getViewedFromResponse } from '../../../../core/models/contentView';
import getSubjectByFilter from '../../../../core/commom/getSubjectByFilter';
import CardLoader from '../cardLoading';
import ContentCard from '../../../../core/components/organism/ContentCard';
import SectionHeader from '../sectionHeader';
import FilterModal from '../ModalFilter';
import DisciplinesEmptyState from '../EmptyState';
import { PopUpModal } from '../../../Gamification/components/Playing/PopUpModal';
import { useShouldShowPopUpScore } from '../../../../core/hooks/useShouldShowPopUpScore';
import { TagProps } from '../../../../core/components/atoms/Tag';
import { Tag } from '../../../../core/components/atoms/Tag';
import { useUserStore } from '../../../Auth/stores/authStore';
import FreemiumLockModal from '../../../../core/components/organism/FreemiumLockModal';
import { useAccess } from '../../../../core/accessControl/useAccess';
import { openFreemiumUpsell } from '../../../../core/stores/freemiumUpsellStore';

interface ILessonList {
  module: string;
  discipline: Colors;
  url: string;
}

interface LessonSection {
  title: string;
  data: ILesson[];
}

const filterLessonsByType = (lessons: ILesson[], type: string): ILesson[] => {
  return lessons.filter((lesson) => lesson.type === type);
};

const YEAR_SLUG_REGEX = /\b(19|20)\d{2}\b/;

const isYearSlug = (slug: string) => YEAR_SLUG_REGEX.test(slug);

const lessonHasSlug = (lesson: ILesson, slug: string) =>
  lesson.subjects.some((subject) => subject.slug === slug);

const lessonMatchesAnySlug = (lesson: ILesson, slugs: string[]) =>
  lesson.subjects.some((subject) => slugs.includes(subject.slug));

const filterLessonsBySelectedSubjects = (
  lessons: ILesson[],
  selectedSubjects: string[]
): ILesson[] => {
  const years = selectedSubjects.filter(isYearSlug);
  const strings = selectedSubjects.filter((slug) => !isYearSlug(slug));

  // 3 & 4: two or more years, or two or more strings (except 1 year + N strings)
  if (years.length >= 2 || (strings.length >= 2 && years.length !== 1)) {
    return lessons.filter((lesson) =>
      lessonMatchesAnySlug(lesson, selectedSubjects)
    );
  }

  // 1: one year and one string → year AND string
  if (years.length === 1 && strings.length === 1) {
    const [year, str] = [years[0], strings[0]];
    return lessons.filter(
      (lesson) => lessonHasSlug(lesson, year) && lessonHasSlug(lesson, str)
    );
  }

  // 2: one year and two or more strings → (year AND string1) OR (year AND string2) ...
  if (years.length === 1 && strings.length >= 2) {
    const year = years[0];
    return lessons.filter((lesson) =>
      strings.some(
        (str) => lessonHasSlug(lesson, year) && lessonHasSlug(lesson, str)
      )
    );
  }

  return lessons.filter((lesson) =>
    lessonMatchesAnySlug(lesson, selectedSubjects)
  );
};

export default function LessonList({ module, discipline, url }: ILessonList) {
  const navigate = useNavigate();

  const { mutate, isPending } = useLesson();
  const { selectedSubjects } = useNewSubjectStore();
  const { lessons, setLessons } = useLessonStore();
  const { mutate: finishLesson, data: finishLessonData } = useFinishLesson();
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);

  const { resetFilters, type, status, favorites, incidence } = useFilterConfigStore();
  const { setAllLessons } = useLessonContentStore();
  const { shouldShowPopUpScore } = useShouldShowPopUpScore();
  const { isContentLocked, isFreemiumUnlocked } = useAccess();

  const [main, setMain] = useState<ILesson[]>([]);
  const [knowmore, setKnowmore] = useState<ILesson[]>([]);
  const [summary, setSummary] = useState<ILesson[]>([]);
  const [showModalScore, setShowModalScore] = useState<boolean>(false);

  useEffect(() => {
    if (lessons?.length > 0) {
      filterLessons();
    }
  }, [lessons, selectedSubjects]);

  useEffect(() => {
    if (module) {
      mutate(module);
    }
  }, []);

  //Ao finalizar o finish lesson, registrar no array, não fazer outra request
  const callbackFav = (data: IResponseFavorite) => {
    if (data.ok) {
      const newLessons = lessons.map((lesson) =>
        lesson._id === data.targetId
          ? { ...lesson, favorite: !lesson.favorite }
          : lesson
      );
      setLessons(newLessons);
    }
  };

  const filterLessons = useCallback(() => {
    if (!lessons?.length) return;

    let filteredData = [...lessons];

    if (selectedSubjects?.length > 0) {
      filteredData = filterLessonsBySelectedSubjects(
        filteredData,
        selectedSubjects
      );
    }

    if (type?.length > 0) {
      filteredData = filteredData.filter((it) => type.includes(it.type));
    }

    if (status?.length === 1) {
      if (status.includes('not_played')) {
        filteredData = filteredData.filter((it) => !it.done);
      } else {
        filteredData = filteredData.filter((it) => it.done);
      }
    }

    if (favorites) {
      filteredData = filteredData.filter((it) => it.favorite);
    }

    if (incidence?.length > 0) {
      filteredData = filteredData.filter((it) => incidence.includes(it.incidence));
    }

    setMain(filterLessonsByType(filteredData, 'main'));
    setKnowmore(filterLessonsByType(filteredData, 'knowmore'));
    setSummary(filterLessonsByType(filteredData, 'summary'));
    setAllLessons(filteredData);
  }, [lessons, selectedSubjects, type, status, favorites, incidence]);

  const lessonSections: LessonSection[] = useMemo(
    () => [
      { title: 'Principal', data: main },
      { title: 'Resumo', data: summary },
      { title: 'Mais', data: knowmore },
    ],
    [main, summary, knowmore]
  );

  function dispatchSetContentAsDoneEvent(id: string) {
    const setContentAsDone = new CustomEvent('setContentAsDone', {
      detail: {
        contentId: id,
      },
    });
    window.dispatchEvent(setContentAsDone);
  }

  const onFinish = (id: string, slug: string) => {
    if (!slug) return;

    // Toggle: cada clique inverte o status. Enviamos explicitamente o próximo
    // estado (`viewed`). O `useFinishLesson` já atualiza o `useLessonStore` com o
    // `viewStatus.viewed` da resposta (fonte da verdade), então aqui só
    // disparamos o evento de "concluído" quando ficou visto.
    const nextDone = !lessons.find((it) => it.slug === slug)?.done;
    setLoadingSlug(slug);
    finishLesson(
      { slug, body: { origin: 'free_browsing', viewed: nextDone } },
      {
        onSuccess: (res) => {
          if (getViewedFromResponse(res)) dispatchSetContentAsDoneEvent(id);
        },
        onSettled: () => setLoadingSlug(null),
      }
    );
  };

  useEffect(() => {
    if (!finishLessonData) return;

    if (finishLessonData.result === 'created' && shouldShowPopUpScore) {
      setShowModalScore(true);
    }
  }, [finishLessonData, shouldShowPopUpScore]);

  if (isPending || !lessons) {
    return (
      <Fragment>
        {Array.from({ length: 10 }).map((_, index) => (
          <CardLoader key={index} />
        ))}
      </Fragment>
    );
  }

  if (main?.length === 0) {
    return (
      <div className='flex flex-col gap-lg'>
        <FilterModal
          title='Filtrar videoaulas'
          onSubmit={filterLessons}
          reset={resetFilters}
          modalName='normal-filter'
          type='videoaula'
        />
        <DisciplinesEmptyState />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-lg'>
      <FilterModal
        title='Filtrar videoaulas'
        onSubmit={filterLessons}
        reset={resetFilters}
        modalName='normal-filter'
        type='videoaula'
      />

      {showModalScore && (
        <PopUpModal
          isOpen={showModalScore}
          onClose={() => setShowModalScore(false)}
          score={5}
        />
      )}
      {lessonSections.map((section, index) => (
        !!section.data?.length && (
          <Fragment key={index}>
            <SectionHeader title={section.title}>
              <Typography size='small'>
                {section.data?.length || 0}{' '}
                {section.data?.length === 1 ? 'videoaula' : 'videoaulas'}
              </Typography>
            </SectionHeader>
            {section.data.map((it, i) => {
              const locked = isContentLocked(it);
              return (
                <ContentCard
                  key={i}
                  content='videoaula'
                  title={it.title}
                  slug={it.slug}
                  icon='play_circle'
                  done={it.done}
                  firstViewedAt={it.firstViewedAt}
                  favorite={it.favorite}
                  color={discipline}
                  action={() =>
                    locked
                      ? openFreemiumUpsell()
                      : navigate(
                        url +
                        `/assunto/${it.subjects[0].slug}/videoaula/${it.slug}`
                      )
                  }
                  subject={getSubjectByFilter(it.subjects, selectedSubjects)}
                  hasCallback
                  onDone={(id: string, slug: string) => onFinish(id, slug)}
                  doneLoading={loadingSlug === it.slug}
                  callback={(data) => callbackFav(data)}
                  id={it.id}
                  locked={locked}
                  freemiumUnlocked={isFreemiumUnlocked(it)}
                >
                  <div className='flex w-fit flex-row gap-lg'>
                    <Typography
                      size='small'
                      className=' transition delay-100 ease-in-out group-hover:text-card-color-default'
                    >
                      {formatTime(it.duration)}
                    </Typography>
                    <Typography
                      size='small'
                      className=' transition delay-100 ease-in-out group-hover:text-card-color-default'
                    >
                      {it.teacher?.length ? `Prof. ${it.teacher[0]?.name}` : ''}
                    </Typography>
                    {it.incidence > 0 && (
                      <Tag
                        variant={`incidencia${it.incidence}` as TagProps['variant']}
                      >{`Incidência ${it.incidence}`}</Tag>
                    )}
                  </div>
                </ContentCard>
              );
            })}
          </Fragment>
        )))}
    </div>
  );
}
